import * as React from 'react';
import { Input, Outside, TInputSize } from '@cian/ui-kit';
import * as styles from './RegionSuggestDesktop.css';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY, TAB_KEY } from '../../../../constants/keyboard';
import { IRegion } from '../../../../types/requestForm';
import { ClearButton } from '../ClearButton';
import { GeoSuggest, IGeoSuggestRef } from '../GeoSuggest';

export interface IRegionSuggestDesktopProps {
  disabled?: boolean;
  loading?: boolean;
  name?: string;
  onInputChange(text: string): void;
  onSelect(region: IRegion): void;
  placeholder?: string;
  size?: TInputSize;
  suggests: IRegion[];
  value: string;
  invalid?: boolean;
}

export const RegionSuggestDesktop: React.FC<IRegionSuggestDesktopProps> = ({
  onInputChange,
  onSelect,
  suggests,
  value,
  ...inputProps
}) => {
  const [isSuggestVisible, setSuggestVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const geoSuggestRef = React.useRef<IGeoSuggestRef>(null);

  const openSuggests = React.useCallback(() => {
    setSuggestVisible(true);
  }, []);

  const closeSuggests = React.useCallback(() => {
    setSuggestVisible(false);
  }, []);

  const selectRegion = React.useCallback(
    (region: IRegion) => {
      onSelect(region);
      closeSuggests();
      inputRef.current?.blur();
    },
    [onSelect, closeSuggests],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      onInputChange(value);
    },
    [onInputChange],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case ARROW_UP_KEY:
          event.preventDefault();
          geoSuggestRef.current?.setActivePrev();
          break;
        case ARROW_DOWN_KEY:
          event.preventDefault();
          geoSuggestRef.current?.setActiveNext();

          break;
        case ENTER_KEY:
          event.preventDefault();
          geoSuggestRef.current?.selectActive();
          break;
        case TAB_KEY:
          closeSuggests();
          break;
      }
    },
    [closeSuggests],
  );

  const handleClear = React.useCallback(async () => {
    inputRef.current?.focus();

    //NOTE: Предотвращает закрытие саджеста между кликом и фокусом
    setTimeout(() => {
      onInputChange('');
    }, 0);
  }, [onInputChange]);

  return (
    <Outside onOutside={closeSuggests} active={isSuggestVisible}>
      <div className={styles['region-suggest']} data-testid="RegionSuggestDesktop">
        <Input
          {...inputProps}
          inputRef={inputRef}
          rightAdornment={value && <ClearButton onClick={handleClear} />}
          value={value}
          onChange={handleChange}
          onFocus={openSuggests}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          role="combobox"
        />
        {isSuggestVisible && suggests.length > 0 && (
          <div className={styles['suggests']}>
            <GeoSuggest ref={geoSuggestRef} suggests={suggests} onSelect={selectRegion} />
          </div>
        )}
      </div>
    </Outside>
  );
};
